import React, { useEffect, useState } from "react";
import { Calendar } from "react-calendar"; // react-calendar 패키지를 사용한다고 가정
import { useLazyQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import clsx from "clsx";
import { formatDateToKoreaTime } from "../../../utils/date/format-date";
import type { Store } from "../../../__generated__/graphql";

/** ReserveCalendar에 필요한 최소 Store 타입 (id, reserveTime) */
type StoreForReserveCalendar = Pick<Store, "id" | "reserveTime">;

const calendarStyles = `
  .react-calendar {
    width: 100% !important;
    max-width: none !important;
    border: none !important;
  }

  .react-calendar__tile {
    text-align: left !important;
    padding: 1.5em 0.5em !important;
  }
  
  .react-calendar__month-view__days__day {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-center !important;
    text-align: center !important;
  }

  .react-calendar__tile--active {
    background: none !important;
    color: inherit !important;
  }

  .react-calendar__tile--now {
    background: none !important;
    color: inherit !important;
  }

  .highlight {
    background-color: green; /* 예약 가능 날짜 색상 */
    color: white; /* 텍스트 색상 */
  }
  .hidden {
    display: none; /* 숨기기 */
  }

  .not-reservable {
    background-color: red; /* 예약 불가능 날짜 색상 */
    color: white; /* 텍스트 색상 */
  }

  /* 주말 색상 변경 */
  .react-calendar__month-view__days__day--weekend {
    color: inherit !important;
  }

  .react-calendar__navigation__prev2-button,
  .react-calendar__navigation__next2-button {
    display: none;
  }
`;

export const GET_RESERVE_EXCEPTIONS_BY_STORE_ID = gql`
  query GetReserveExceptionsByStoreId($storeId: Int!) {
    getReserveExceptionsByStoreId(storeId: $storeId) {
      id
      dateString
      start
      end
      isReservable
      note
    }
  }
`;

interface ReserveException {
  id: string;
  dateString: string;
  start?: string;
  end?: string;
  isReservable: boolean;
  note?: string;
}

interface DateStatus {
  date: string;
  isReservable: boolean;
  dateString: string;
  isSelected?: boolean;
}

const ReserveCalendar = ({ store }: { store: StoreForReserveCalendar }) => {
  const [dateStatuses, setDateStatuses] = useState<DateStatus[]>([]);
  const [GetReserveExceptionsByStoreId] = useLazyQuery(
    GET_RESERVE_EXCEPTIONS_BY_STORE_ID,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data) {
          const reserveExceptions = data.getReserveExceptionsByStoreId;
          const updatedStatuses = dateStatuses.map((status) => {
            const exception = reserveExceptions.find(
              (ex: ReserveException) => ex.dateString === status.date
            );
            if (exception) {
              return {
                ...status,
                isReservable: exception.isReservable,
              };
            }
            return status;
          });
          setDateStatuses(updatedStatuses);
        }
      },
    }
  );
  useEffect(() => {
    const currentDate = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      1
    );
    const daysInMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    ).getDate();
    const daysInMonth2 = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 2,
      0
    ).getDate();
    const reserveTime = store?.reserveTime;
    const initialStatuses: DateStatus[] = [];

    if (reserveTime) {
      for (let i = 0; i < daysInMonth + daysInMonth2; i++) {
        const dateToCheck = new Date();
        dateToCheck.setDate(currentDate.getDate() + i);
        const dayOfWeek = ((dateToCheck.getDay() + 6) % 7).toString();
        const isReservable = reserveTime[dayOfWeek]?.enabled;
        if (isReservable) {
          initialStatuses.push({
            date: formatDateToKoreaTime(dateToCheck),
            isReservable: isReservable || false,
            dateString: formatDateToKoreaTime(dateToCheck),
          });
        }
      }
    }
    setDateStatuses(initialStatuses);
    GetReserveExceptionsByStoreId({
      variables: {
        storeId: store.id,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const today = new Date();
  const startOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0); // 다음달 마지막 날
  return (
    <div className="my-4">
      <hr className="my-4" />
      <style>{calendarStyles}</style>
      <div className="flex items-end justify-between mb-4"></div>
      <div className="flex justify-center mb-2">
        <div className="flex items-center mr-4">
          <div className="bg-blue-400 rounded-full w-4 h-4 mr-2"></div>
          <span>열어놓은 날</span>
        </div>
        <div className="flex items-center">
          <div className="bg-orange-400 rounded-full w-4 h-4 mr-2"></div>
          <span>닫아놓은 날</span>
        </div>
      </div>
      <Calendar
        className="rounded-xl shadow-lg border"
        calendarType="gregory"
        minDate={startOfThisMonth}
        maxDate={endOfNextMonth}
        tileContent={({ date }) => {
          const dateStatus = dateStatuses.find(
            (status) => status.date === formatDateToKoreaTime(date)
          );
          if (dateStatus) {
            return (
              <div className="flex justify-center items-center w-full h-full">
                <div
                  className={clsx(
                    dateStatus.isReservable ? "bg-blue-400" : "bg-orange-400",
                    "rounded-full w-4 h-4 flex justify-center items-center transition-transform duration-300 transform"
                  )}
                ></div>
              </div>
            );
          }
          return null; // 예약 예외가 없는 날짜는 null 반환
        }}
      />
    </div>
  );
};

export default ReserveCalendar;
